/**
 * Onboarding Candidate Edit Page — update candidate details via BLL PUT.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

const SOURCE_OPTIONS = [
  { value: 'portal', label: 'Job Portal' },
  { value: 'referral', label: 'Referral' },
  { value: 'agency', label: 'Agency' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'walk-in', label: 'Walk-in' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'applied', label: 'Applied' },
  { value: 'screening', label: 'Screening' },
  { value: 'offered', label: 'Offered' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'joining', label: 'Joining' },
  { value: 'joined', label: 'Joined' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

interface Field {
  label: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}

function Field({ label, required, optional, children }: Field) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1.5">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
        {optional && <span className="text-muted-foreground text-xs ml-1">(optional)</span>}
      </label>
      {children}
    </div>
  );
}

async function callBLL(path: string, method: string, body?: unknown) {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

export function OnboardingEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [positionApplied, setPositionApplied] = React.useState('');
  const [expectedJoinDate, setExpectedJoinDate] = React.useState('');
  const [offeredCtc, setOfferedCtc] = React.useState('');
  const [source, setSource] = React.useState('');
  const [status, setStatus] = React.useState('applied');
  const [notes, setNotes] = React.useState('');

  React.useEffect(() => {
    if (!id) return;
    callBLL(`/v1/hros/onboarding/candidates/${id}`, 'GET')
      .then((res) => {
        if (res.success && res.data) {
          const c = res.data;
          setFullName(c.full_name ?? '');
          setEmail(c.email ?? '');
          setPhone(c.phone ?? '');
          setPositionApplied(c.position_applied ?? '');
          setExpectedJoinDate(c.expected_join_date ? c.expected_join_date.slice(0, 10) : '');
          setOfferedCtc(c.offered_ctc != null ? String(c.offered_ctc) : '');
          setSource(c.source ?? '');
          setStatus(c.status ?? 'applied');
          setNotes(c.notes ?? '');
        } else {
          setError(res.error?.message ?? 'Failed to load candidate.');
        }
      })
      .catch(() => setError('Network error.'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!fullName.trim()) { setError('Full name is required.'); return; }
    if (!positionApplied.trim()) { setError('Position applied is required.'); return; }

    const body: Record<string, unknown> = {
      full_name: fullName.trim(),
      position_applied: positionApplied.trim(),
      status,
    };
    if (email.trim()) body.email = email.trim();
    if (phone.trim()) body.phone = phone.trim();
    if (expectedJoinDate) body.expected_join_date = expectedJoinDate;
    if (offeredCtc) body.offered_ctc = Number(offeredCtc);
    if (source) body.source = source;
    if (notes.trim()) body.notes = notes.trim();

    setSubmitting(true);
    try {
      const res = await callBLL(`/v1/hros/onboarding/candidates/${id}`, 'PUT', body);
      if (res.success) {
        navigate(`/onboarding/${id}`);
      } else {
        setError(res.error?.message ?? 'Failed to update candidate.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="flex flex-col gap-6 p-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/onboarding/${id}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Edit Candidate</h1>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <Card>
          <CardContent className="pt-5 space-y-5">
            <Field label="Full Name" required>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </Field>

            <Field label="Email" optional>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </Field>

            <Field label="Phone" optional>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </Field>

            <Field label="Position Applied" required>
              <input type="text" value={positionApplied} onChange={(e) => setPositionApplied(e.target.value)} placeholder="Senior Software Engineer"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </Field>

            <Field label="Status">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Expected Join Date" optional>
              <input type="date" value={expectedJoinDate} onChange={(e) => setExpectedJoinDate(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </Field>

            <Field label="Offered CTC (Annual)" optional>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">₹</span>
                <input type="number" min={0} step={1000} value={offeredCtc} onChange={(e) => setOfferedCtc(e.target.value)} placeholder="1200000"
                  className="w-full border rounded-md pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </Field>

            <Field label="Source" optional>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger><SelectValue placeholder="Select recruitment source…" /></SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Notes" optional>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional notes…" rows={3}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
            </Field>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex gap-3 pt-1">
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save Changes'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate(`/onboarding/${id}`)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

export default OnboardingEditPage;
